import { useLayoutEffect, useRef, useState } from "react";
import { MAIN_CONTENT_ID } from "common/constants";

/**
 * Returns a ref and the distance (px) between the top of the page scroller
 * and the referenced element, so a full-height tool rendered under the
 * new-look page intro can size itself to the remaining height.
 */
const useFillRemainingHeight = <T extends HTMLElement>(enabled: boolean) => {
  const ref = useRef<T>(null);
  const [offset, setOffset] = useState(0);

  useLayoutEffect(() => {
    if (!enabled) return;
    const scroller = document.getElementById(MAIN_CONTENT_ID);
    if (!scroller) return;

    const update = () => {
      if (!ref.current) return;
      const top =
        ref.current.getBoundingClientRect().top -
        scroller.getBoundingClientRect().top +
        scroller.scrollTop;
      setOffset(Math.max(0, Math.round(top)));
    };

    update();
    const observer = new ResizeObserver(update);
    if (scroller.firstElementChild)
      observer.observe(scroller.firstElementChild);
    window.addEventListener("resize", update);
    return () => {
      observer.disconnect();
      window.removeEventListener("resize", update);
    };
  }, [enabled]);

  return { ref, offset };
};

export default useFillRemainingHeight;
